// Top navigation bar for authenticated pages.
// Translucent + backdrop-blur over the hero gradient, with brand on the left,
// nav links in the center, and user menu on the right.

import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Typography,
  useMediaQuery,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import ExtensionIcon from '@mui/icons-material/Extension';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import { useTranslation } from 'react-i18next';
import Brand from './Brand';
import LanguageSwitcher from './LanguageSwitcher';
import AlertsBell from './AlertsBell';
import { colors } from '../theme';
import { isRaphaDomain } from '../services/subdomain';
import { useFeatures } from '../services/features';
import { useCurrentUser } from '../services/useCurrentUser';

interface NavItem {
  label: string;
  href: string;
}

export default function AppHeader({ showNav = true }: { showNav?: boolean }) {
  const { t } = useTranslation('common');

  // Read the role from the decoded JWT — drives the rapha-only Fit nav
  // entry below and the Subscription menu item visibility further down.
  const currentUser = useCurrentUser();

  // Wave 14 — /sessions list and /positions{,/analytics} replaced by the
  // unified Dashboard. The lightweight Positions reference page was also
  // dropped — position colors + canonical labels live alongside the
  // analytics on /dashboard now.
  // Wave 18.1 — "Fit" entry only surfaces on rapha.aero.chat (not on
  // www.aero.chat, even for role='rapha' accounts). Rapha tools live on
  // the Rapha subdomain; a rapha member on aero.chat sees the general
  // product surface and would navigate to rapha.aero.chat for Fit work.
  // Wave 20.37 — Feed is now capability-gated (Rapha sees it too but
  // without drops / social). Discover is drops-social, stays gone on
  // Rapha. Uses useFeatures() as the single source of truth.
  const features = useFeatures();
  const isRapha = isRaphaDomain();
  const NAV_ITEMS: NavItem[] = [
    ...(features.wake ? [{ label: 'Wake', href: '/wake' }] : []),
    { label: t('nav.session'), href: '/session' },
    { label: 'Feed', href: '/feed' },
    { label: t('nav.aero'), href: '/dashboard/aero' },
    ...(features.discoverPeople
      ? [{ label: 'Discover', href: '/discover' }]
      : []),
    ...(isRapha && currentUser.role === 'rapha'
      ? [{ label: t('nav.fit'), href: '/rapha/fit' }]
      : []),
    // Wave 20.18 — superadmin nav entry. Same account can log into
    // aero.chat or rapha.aero.chat; the link shows in either.
    ...(currentUser.role === 'superadmin'
      ? [{ label: 'Admin', href: '/admin' }]
      : []),
    { label: t('nav.about'), href: '/about' },
  ];
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [user, setUser] = useState<{
    id?: number;
    firstName?: string;
    email?: string;
    avatarUpdatedAt?: string | null;
  } | null>(null);
  const isMobile = useMediaQuery('(max-width:800px)');
  const activePath = window.location.pathname;
  // Wave 20.49d — sync token check so we know at first render
  // whether to show the app nav. If there's no token, the whole
  // logged-in nav row (Wake / Session / Feed / ...) stays hidden —
  // those routes just bounce to login and shouldn't be shown to
  // public visitors.
  const isAuthed = React.useMemo(() => {
    try {
      return !!localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || '');
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (!isAuthed) return;
    fetch(`${process.env.REACT_APP_API}/users`, {
      headers: { Authorization: `Bearer ${localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || '')}` },
    })
      .then((r) => r.json())
      .then((u) => setUser(u))
      .catch(() => {});
  }, [isAuthed]);

  const initials = user?.firstName
    ? user.firstName.charAt(0).toUpperCase()
    : user?.email
    ? user.email.charAt(0).toUpperCase()
    : 'A';

  const handleLogout = () => {
    localStorage.removeItem(process.env.REACT_APP_TOKEN_KEY || '');
    window.location.href = '/login';
  };

  return (
    <AppBar position="sticky" elevation={0}>
      <Toolbar sx={{ minHeight: 68, px: { xs: 2, md: 4 } }}>
        {/* Mobile-only hamburger. On narrow viewports the horizontal
            nav row (Wake / Session / Feed / …) is hidden — without
            this button the rider had to rotate to landscape to reach
            any section. */}
        {showNav && isAuthed && isMobile && (
          <IconButton
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation menu"
            sx={{
              mr: 1,
              color: colors.text,
              border: `1px solid ${colors.border}`,
              width: 40,
              height: 40,
              '&:hover': { borderColor: colors.brand, color: colors.brand },
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Brand size="sm" href="/" />

        {showNav && isAuthed && !isMobile && (
          <Box sx={{ display: 'flex', gap: 1, ml: 6, flexGrow: 1 }}>
            {NAV_ITEMS.map((item) => {
              const active = activePath === item.href;
              return (
                <Button
                  key={item.href}
                  href={item.href}
                  sx={{
                    color: active ? colors.text : colors.textMuted,
                    fontWeight: active ? 600 : 500,
                    backgroundColor: active ? 'rgba(255,255,255,0.04)' : 'transparent',
                    position: 'relative',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.06)',
                      color: colors.text,
                    },
                    '&::after': active
                      ? {
                          content: '""',
                          position: 'absolute',
                          bottom: 6,
                          left: '20%',
                          right: '20%',
                          height: 2,
                          backgroundColor: colors.brand,
                          borderRadius: 1,
                        }
                      : {},
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Box>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {isRaphaDomain() && (
          <Box sx={{ mr: 2 }}>
            <LanguageSwitcher />
          </Box>
        )}

        {user ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {!isMobile && (
                <Typography variant="body2" sx={{ color: colors.textMuted }}>
                  {user.firstName || user.email}
                </Typography>
              )}
              {/* Wave 20.49at — Alerts bell with unread badge.
                  Polls /alerts/unread-count every 60 s so the
                  badge stays live without a full-page refresh. */}
              <AlertsBell />
              <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                  p: 0,
                  border: `1px solid ${colors.border}`,
                  '&:hover': { borderColor: colors.brand },
                }}
              >
                <Avatar
                  src={
                    user?.id && user?.avatarUpdatedAt
                      ? `${process.env.REACT_APP_API}/users/${user.id}/avatar?v=${new Date(user.avatarUpdatedAt).getTime()}`
                      : undefined
                  }
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: colors.surfaceElevated,
                    color: colors.brand,
                    fontSize: '0.875rem',
                    fontWeight: 700,
                  }}
                >
                  {!user?.avatarUpdatedAt && initials}
                </Avatar>
              </IconButton>
            </Box>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 1,
                    minWidth: 200,
                    backgroundColor: colors.surfaceElevated,
                    border: `1px solid ${colors.border}`,
                  },
                },
              }}
            >
              <MenuItem component="a" href="/account">
                <PersonOutlineIcon sx={{ mr: 1.5, fontSize: 20 }} /> {t('menu.account')}
              </MenuItem>
              {/* Rapha members have unconditional access via their
                  membership — surfacing a subscription option would
                  misrepresent how their account works. */}
              {currentUser.role !== 'rapha' && (
                <MenuItem component="a" href="/subscription">
                  <CardMembershipIcon sx={{ mr: 1.5, fontSize: 20 }} /> {t('menu.subscription')}
                </MenuItem>
              )}
              <MenuItem component="a" href="/integrations">
                <ExtensionIcon sx={{ mr: 1.5, fontSize: 20 }} /> Integrations
              </MenuItem>
              {currentUser.role === 'superadmin' && (
                <MenuItem component="a" href="/admin">
                  <ShieldOutlinedIcon sx={{ mr: 1.5, fontSize: 20 }} /> Admin
                </MenuItem>
              )}
              <MenuItem component="a" href="/account">
                <SettingsOutlinedIcon sx={{ mr: 1.5, fontSize: 20 }} /> {t('menu.settings')}
              </MenuItem>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 1.5, fontSize: 20 }} /> {t('menu.logOut')}
              </MenuItem>
            </Menu>
          </>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Wave 20.49c — About surfaced on the right so the
                homepage (which passes showNav={false} to keep the
                hero clean) still gives public visitors a way to
                read what aero.chat does without scrolling to the
                footer. Shows on both desktop + mobile — the
                homepage doesn't render a hamburger, so this is
                the only path. */}
            <Button
              href="/about"
              sx={{
                color: colors.textMuted,
                fontWeight: 500,
                px: { xs: 1, md: 2 },
                minWidth: 0,
              }}
            >
              {t('nav.about')}
            </Button>
            <Button href="/login" sx={{ color: colors.textMuted }}>
              {t('buttons.logIn')}
            </Button>
            <Button href="/register" variant="contained">
              {t('buttons.getStarted')}
            </Button>
          </Box>
        )}
      </Toolbar>

      {/* Mobile nav drawer — mirrors NAV_ITEMS. Only mounts when the
          hamburger is used, so desktop pays zero runtime cost. */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        slotProps={{
          paper: {
            sx: {
              width: 260,
              backgroundColor: colors.surfaceElevated,
              borderRight: `1px solid ${colors.border}`,
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 2, borderBottom: `1px solid ${colors.border}` }}>
          <Brand size="sm" href="/" />
        </Box>
        <List sx={{ py: 1 }}>
          {NAV_ITEMS.map((item) => {
            const active = activePath === item.href;
            return (
              <ListItemButton
                key={item.href}
                component="a"
                href={item.href}
                onClick={() => setDrawerOpen(false)}
                sx={{
                  py: 1.25,
                  color: active ? colors.brand : colors.text,
                  fontWeight: active ? 700 : 500,
                  backgroundColor: active ? 'rgba(106,255,69,0.06)' : 'transparent',
                  borderLeft: `3px solid ${active ? colors.brand : 'transparent'}`,
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.04)',
                    color: colors.brand,
                  },
                }}
              >
                {item.label}
              </ListItemButton>
            );
          })}
        </List>
      </Drawer>
    </AppBar>
  );
}
